import { useEffect, useState } from "react";
import Select from "../../../../../components/form/Select";
import TextArea from "../../../../../components/form/TextArea";
import { useHub } from "../../../../../core/Global";
import { API_ROUTES } from "../../../../../core/Constants";
import { CancelReasonCompact } from "../../../base/models/CancelReasonItem";

/** The synthetic option that drops the picker and hands over to the free-text box. */
export const CUSTOM_REASON = "custom";

export interface CancelReasonValue {
    /** Set only when a ready-made reason was picked; the wording then comes from the reason itself. */
    cancelReasonId?: string
    /** Set only when the note was typed. */
    note?: string
    /** Which of the two fields is on screen, so a validation message lands on it. */
    errorField: "cancelReasonId" | "note"
}

interface IProps {
    autoFocusNote?: boolean
    onChange: (value: CancelReasonValue) => void
}

/**
 * The reason a job is being called off: one of the account's ready-made cancel reasons, or
 * something written on the spot.
 *
 * Only ONE of the two is ever reported. A picked reason is reported as its id, never as its text —
 * the server takes the wording from the reason, which is what lets a later correction to the reason
 * reach the jobs that were already cancelled with it. An account with no reasons set up sees just
 * the box, exactly as before.
 */
export default function JobCancelReasonField(props: IProps) {

    const hub = useHub();

    const [reasons, reasonsSet] = useState<CancelReasonCompact[]>([]);
    const [loaded, loadedSet] = useState<boolean>(false);
    const [selectedId, selectedIdSet] = useState<string>();
    const [note, noteSet] = useState<string>("");

    useEffect(() => {
        hub.Get(API_ROUTES.CancelReasons + "all", res => {
            if (res.success && res.data) reasonsSet(res.data as CancelReasonCompact[]);
            loadedSet(true);
        })
    }, [])

    // Nothing to choose from means nothing to choose: fall straight through to the box.
    const hasReasons = loaded && reasons.length > 0;
    const writing = !hasReasons || selectedId === CUSTOM_REASON;

    const report = (id?: string, text?: string, isWriting?: boolean) => {
        const errorField = isWriting ? "note" : "cancelReasonId";
        if (id && id !== CUSTOM_REASON) props.onChange({ cancelReasonId: id, errorField })
        else props.onChange({ note: text, errorField })
    }

    useEffect(() => {
        // Report the starting state too, so a parent that submits without touching the field is
        // not left holding a value from a previous cancellation.
        report(selectedId, note, writing);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loaded])

    const source = [
        ...reasons.map(r => ({ id: r.id, name: r.name })),
        { id: CUSTOM_REASON, name: "Write my own note..." }
    ];

    return (
        <div>
            {
                hasReasons &&
                <Select id="drp-cancel-reason" name="cancelReasonId"
                    label="Cancel Reason*" labelUp block
                    placeholder="Choose a reason" deselect={false}
                    source={source} value={selectedId}
                    marginBottom={writing ? 3 : 0}
                    onSelect={val => {
                        selectedIdSet(val);
                        // A reason and a typed note are alternatives, so switching away from the
                        // box must not leave its text behind as the answer.
                        if (val !== CUSTOM_REASON) noteSet("");
                        report(val, "", val === CUSTOM_REASON);
                    }} />
            }

            {
                writing &&
                <TextArea id="txt-cancel-reason-note" name="note"
                    label={hasReasons ? "Note*" : "Cancel Reason*"} labelUp block rows={4}
                    placeholder="Please write why the job is being canceled..."
                    autoFocus={props.autoFocusNote}
                    value={note}
                    onChange={e => {
                        noteSet(e.target.value);
                        report(CUSTOM_REASON, e.target.value, true);
                    }} />
            }
        </div>
    )
}

/** True when the picker has an answer the server will accept. */
export const isCancelReasonValid = (value?: CancelReasonValue) =>
    !!value?.cancelReasonId || (value?.note?.trim().length ?? 0) > 0;

/** Puts the "say why" message beside whichever of the two fields is on screen. */
export const showCancelReasonError = (value?: CancelReasonValue, parentId?: string) =>
    window.showFormError(
        value?.errorField ?? "note",
        value?.errorField === "cancelReasonId" ? "Please choose a cancel reason!" : "Please write why the job is being canceled!",
        true, parentId);
